// Spinner.js — the app's one loading indicator.
//
// Every page in here waits on ClickHouse for something: the team list, the competitions, the
// matches, the report itself. Before this existed each page invented its own "Loading…" line (or
// showed nothing at all), so the same wait looked different depending on which page you were on.
// One component, one ring, one animation — used both as a bare spinner beside a label and as the
// full-block state a panel shows while its data is in flight.
//
// The ring is a CSS border rather than an SVG or an icon-font glyph on purpose: the standalone
// HTML exports (see exportHtml.js) inline the stylesheets, so a border-and-keyframes spinner
// survives an export intact with no external asset to resolve.
import React from 'react';
import './Spinner.css';

// `size` is the ring's diameter in px; the stroke scales with it so a 12px spinner doesn't read
// as a solid dot and a 40px one doesn't read as a hairline.
export function Spinner({ size = 16, className = '', style, ...rest }) {
  const stroke = Math.max(1.5, Math.round(size / 8));
  return (
    <span
      className={`sp-ring ${className}`}
      style={{ width: size, height: size, borderWidth: stroke, ...style }}
      role="status"
      aria-label="Loading"
      {...rest}
    />
  );
}

// Inline "◌ Loading teams…" — for a status line inside a toolbar, a card header or a table cell.
export function LoadingLine({ text = 'Loading…', size = 14, className = '', style }) {
  return (
    <span className={`sp-line ${className}`} style={style}>
      <Spinner size={size} />
      {text ? <span className="sp-line-text">{text}</span> : null}
    </span>
  );
}

// The whole-panel version: a centred ring with a label under it, for the state where the page
// has nothing to show yet. `sub` takes the "…this can take a minute" second line.
export function LoadingBlock({ text = 'Loading…', sub = '', size = 30, className = '', style }) {
  return (
    <div className={`sp-block ${className}`} style={style} role="status" aria-live="polite">
      <Spinner size={size} />
      <div className="sp-block-text">{text}</div>
      {sub ? <div className="sp-block-sub">{sub}</div> : null}
    </div>
  );
}

export default Spinner;
